import { useState, useEffect, useCallback } from 'react';
import Modal from '../../../components/base/Modal';
import { supabase } from '../../../lib/supabase';
import { Deal } from './KanbanSection';
import RequestSampleModal from './RequestSampleModal';

interface DealDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  deal: Deal | null;
  stages: { id: string; label: string; color: string }[];
  onEdit: (deal: Deal) => void;
  onClientUpdated?: () => Promise<void>;
}

interface ClientFull {
  id: string;
  name: string;
  phone: string;
  email: string;
  platform: string;
  followers: number;
  category: string;
  status: string;
  notes: string;
  cpf_cnpj: string;
  tiktok_links: string[];
  gmv_geral: number;
  produtos_divulgados: string;
  comissao_organica: number;
  comissao_trafego: number;
  gmv_interno_7d: number;
  gmv_interno_14d: number;
  gmv_interno_28d: number;
  gmv_interno_30d: number;
  whatsapp_group_link: string;
  videos_7d: number;
  videos_14d: number;
  videos_28d: number;
  videos_30d: number;
  lives_7d: number;
  lives_14d: number;
  lives_28d: number;
  lives_30d: number;
  endereco_cep: string;
  endereco_rua: string;
  endereco_numero: string;
  endereco_complemento: string;
  endereco_bairro: string;
  endereco_cidade: string;
  endereco_estado: string;
  chave_pix: string;
  chave_pix_tipo: string;
  codigo_rastreio: string;
  amostra_enviada: boolean;
  amostra_data_envio: string;
  amostra_observacao: string;
}

type EditSection = 'endereco' | 'pix' | 'amostra' | 'resultados' | 'contato' | null;

export default function DealDetailModal({
  isOpen,
  onClose,
  deal,
  stages,
  onEdit,
  onClientUpdated,
}: DealDetailModalProps) {
  const [client, setClient] = useState<ClientFull | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'resultados'>('info');
  const [editSection, setEditSection] = useState<EditSection>(null);
  const [editData, setEditData] = useState<Partial<ClientFull>>({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [showSampleModal, setShowSampleModal] = useState(false);

  const loadClient = useCallback(async () => {
    if (!deal?.client_id) {
      setClient(null);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('id', deal.client_id)
      .maybeSingle();
    if (error) {
      console.error('Failed to fetch client:', error);
      setClient(null);
    } else {
      setClient(data as ClientFull | null);
    }
    setLoading(false);
  }, [deal?.client_id]);

  useEffect(() => {
    if (isOpen) {
      loadClient();
      setEditSection(null);
      setSaveSuccess(null);
    }
  }, [isOpen, loadClient]);

  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => setSaveSuccess(null), 2500);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);

  const startEdit = (section: EditSection) => {
    if (!client) return;
    setEditSection(section);
    setSaveSuccess(null);

    if (section === 'contato') {
      setEditData({
        phone: client.phone || '',
        email: client.email || '',
        cpf_cnpj: client.cpf_cnpj || '',
      });
    } else if (section === 'endereco') {
      setEditData({
        endereco_cep: client.endereco_cep || '',
        endereco_rua: client.endereco_rua || '',
        endereco_numero: client.endereco_numero || '',
        endereco_complemento: client.endereco_complemento || '',
        endereco_bairro: client.endereco_bairro || '',
        endereco_cidade: client.endereco_cidade || '',
        endereco_estado: client.endereco_estado || '',
      });
    } else if (section === 'pix') {
      setEditData({
        chave_pix: client.chave_pix || '',
        chave_pix_tipo: client.chave_pix_tipo || '',
      });
    } else if (section === 'amostra') {
      setEditData({
        amostra_enviada: client.amostra_enviada || false,
        codigo_rastreio: client.codigo_rastreio || '',
        amostra_data_envio: client.amostra_data_envio ? client.amostra_data_envio.split('T')[0] : '',
        amostra_observacao: client.amostra_observacao || '',
      });
    } else if (section === 'resultados') {
      setEditData({
        gmv_geral: client.gmv_geral || 0,
        comissao_organica: client.comissao_organica || 0,
        comissao_trafego: client.comissao_trafego || 0,
        gmv_interno_7d: client.gmv_interno_7d || 0,
        gmv_interno_14d: client.gmv_interno_14d || 0,
        gmv_interno_28d: client.gmv_interno_28d || 0,
        gmv_interno_30d: client.gmv_interno_30d || 0,
        videos_7d: client.videos_7d || 0,
        videos_14d: client.videos_14d || 0,
        videos_28d: client.videos_28d || 0,
        videos_30d: client.videos_30d || 0,
        lives_7d: client.lives_7d || 0,
        lives_14d: client.lives_14d || 0,
        lives_28d: client.lives_28d || 0,
        lives_30d: client.lives_30d || 0,
        produtos_divulgados: client.produtos_divulgados || '',
        tiktok_links: client.tiktok_links || [],
        whatsapp_group_link: client.whatsapp_group_link || '',
      });
    }
  };

  const cancelEdit = () => {
    setEditSection(null);
    setEditData({});
  };

  const saveEdit = async () => {
    if (!client) return;
    setSaving(true);
    try {
      const updatePayload: Record<string, unknown> = { ...editData, updated_at: new Date().toISOString() };
      
      if (editSection === 'amostra' && editData.amostra_data_envio) {
        updatePayload.amostra_data_envio = new Date(editData.amostra_data_envio as string).toISOString();
      }
      if (editSection === 'amostra' && !editData.amostra_data_envio) {
        updatePayload.amostra_data_envio = null;
      }

      const { error } = await supabase
        .from('clients')
        .update(updatePayload)
        .eq('id', client.id);

      if (error) throw error;

      await loadClient();
      if (onClientUpdated) await onClientUpdated();
      
      const sectionLabels: Record<string, string> = {
        contato: 'Contato',
        endereco: 'Endereço',
        pix: 'PIX',
        amostra: 'Amostra',
        resultados: 'Resultados',
      };
      setSaveSuccess(sectionLabels[editSection || ''] || 'Dados');
      setEditSection(null);
      setEditData({});
    } catch (error) {
      console.error('Erro ao salvar:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: string, value: unknown) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  if (!deal) return null;

  const stage = stages.find((s) => s.id === deal.stage);
  const priorityConfig: Record<string, { label: string; color: string; dot: string }> = {
    high: { label: 'Alta', color: 'bg-rose-50 text-rose-600', dot: 'bg-rose-500' },
    medium: { label: 'Média', color: 'bg-amber-50 text-amber-600', dot: 'bg-amber-500' },
    low: { label: 'Baixa', color: 'bg-emerald-50 text-emerald-600', dot: 'bg-emerald-500' },
  };
  const priority = priorityConfig[deal.priority] || priorityConfig.medium;

  const formatCurrency = (val: number) =>
    `R$ ${Number(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const hasAddress = client && (client.endereco_cep || client.endereco_rua || client.endereco_cidade);
  const hasPix = client && client.chave_pix;
  const hasSample = client && (client.codigo_rastreio || client.amostra_enviada);

  const fullAddress = client
    ? [
        client.endereco_rua,
        client.endereco_numero ? `nº ${client.endereco_numero}` : '',
        client.endereco_complemento,
        client.endereco_bairro,
        client.endereco_cidade && client.endereco_estado
          ? `${client.endereco_cidade} - ${client.endereco_estado}`
          : client.endereco_cidade || client.endereco_estado,
        client.endereco_cep ? `CEP: ${client.endereco_cep}` : '',
      ].filter(Boolean).join(', ')
    : '';

  const pixTipoLabel: Record<string, string> = {
    cpf: 'CPF',
    cnpj: 'CNPJ',
    email: 'E-mail',
    telefone: 'Telefone',
    aleatoria: 'Chave Aleatória',
  };

  const gmvData = client
    ? [
        { label: '7 dias', value: client.gmv_interno_7d, field: 'gmv_interno_7d' },
        { label: '14 dias', value: client.gmv_interno_14d, field: 'gmv_interno_14d' },
        { label: '28 dias', value: client.gmv_interno_28d, field: 'gmv_interno_28d' },
        { label: '30 dias', value: client.gmv_interno_30d, field: 'gmv_interno_30d' },
      ]
    : [];

  const videosData = client
    ? [
        { label: '7d', value: client.videos_7d, field: 'videos_7d' },
        { label: '14d', value: client.videos_14d, field: 'videos_14d' },
        { label: '28d', value: client.videos_28d, field: 'videos_28d' },
        { label: '30d', value: client.videos_30d, field: 'videos_30d' },
      ]
    : [];

  const livesData = client
    ? [
        { label: '7d', value: client.lives_7d, field: 'lives_7d' },
        { label: '14d', value: client.lives_14d, field: 'lives_14d' },
        { label: '28d', value: client.lives_28d, field: 'lives_28d' },
        { label: '30d', value: client.lives_30d, field: 'lives_30d' },
      ]
    : [];

  const maxVideos = Math.max(...videosData.map((v) => Number(v.value || 0)), 1);
  const maxLives = Math.max(...livesData.map((v) => Number(v.value || 0)), 1);

  const EditButton = ({ section }: { section: EditSection }) => (
    <button
      onClick={(e) => { e.stopPropagation(); startEdit(section); }}
      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-[#004aad] hover:bg-[#5de0e6]/10 rounded-md transition-all cursor-pointer whitespace-nowrap"
      title="Editar"
    >
      <i className="ri-pencil-line text-xs"></i>
      Editar
    </button>
  );

  const SaveCancelButtons = () => (
    <div className="flex items-center gap-2 mt-3">
      <button
        onClick={saveEdit}
        disabled={saving}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#5de0e6] hover:bg-[#004aad] rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
      >
        {saving ? (
          <i className="ri-loader-4-line text-xs animate-spin"></i>
        ) : (
          <i className="ri-check-line text-xs"></i>
        )}
        Salvar
      </button>
      <button
        onClick={cancelEdit}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all cursor-pointer whitespace-nowrap"
      >
        Cancelar
      </button>
    </div>
  );

  const inputClass = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5de0e6]/20 focus:border-[#5de0e6] transition-all';
  const labelClass = 'text-[11px] font-medium text-gray-500 mb-1 block';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={deal.title}
      subtitle="Detalhes do Acompanhamento"
      size="lg"
    >
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line text-3xl text-[#5de0e6] animate-spin"></i>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Toast de sucesso */}
          {saveSuccess && (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 border border-emerald-100 rounded-xl animate-[fadeIn_0.2s_ease-out]">
              <i className="ri-check-double-line text-emerald-500"></i>
              <span className="text-sm text-emerald-700 font-medium">{saveSuccess} atualizado com sucesso!</span>
            </div>
          )}

          {/* Header com info do deal */}
          <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#5de0e6] to-[#004aad] rounded-xl flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-lg">
                  {(deal.client_name || 'N').charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {deal.client_name || 'Sem creator'}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  {stage && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md border"
                      style={{
                        backgroundColor: `${stage.color}15`,
                        color: stage.color,
                        borderColor: `${stage.color}30`,
                      }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: stage.color }}></span>
                      {stage.label}
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md ${priority.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${priority.dot}`}></span>
                    Atenção: {priority.label}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                onClose();
                setTimeout(() => onEdit(deal), 200);
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#004aad] bg-[#5de0e6]/10 hover:bg-[#5de0e6]/20 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line text-sm"></i>
              Editar Deal
            </button>
          </div>

          {/* Info rápida do deal */}
          <div className="grid grid-cols-3 gap-3">
            {deal.assigned_name && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Responsável</p>
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 bg-gray-200 rounded-md flex items-center justify-center">
                    <i className="ri-user-line text-[10px] text-gray-500"></i>
                  </div>
                  <span className="text-xs font-medium text-gray-700">{deal.assigned_name}</span>
                </div>
              </div>
            )}
            {deal.supervisor_name && (
              <div className="bg-amber-50/60 rounded-lg p-3">
                <p className="text-[10px] text-amber-500 uppercase tracking-wide mb-1">Supervisor</p>
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 bg-amber-100 rounded-md flex items-center justify-center">
                    <i className="ri-shield-user-line text-[10px] text-amber-500"></i>
                  </div>
                  <span className="text-xs font-medium text-amber-700">{deal.supervisor_name}</span>
                </div>
              </div>
            )}
            {deal.expected_close_date && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Previsão</p>
                <div className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-xs text-gray-400"></i>
                  <span className="text-xs font-medium text-gray-700">
                    {new Date(deal.expected_close_date).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Tags */}
          {deal.tags && deal.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {deal.tags.map((tag) => (
                <span key={tag} className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-teal-50 text-teal-600 border border-teal-100">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Descrição */}
          {deal.description && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Descrição</p>
              <p className="text-sm text-gray-600">{deal.description}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 rounded-full p-1">
            <button
              onClick={() => { setActiveTab('info'); cancelEdit(); }}
              className={`flex-1 text-sm font-medium py-2 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'info' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-user-line mr-1.5"></i>
              Endereço / PIX / Amostra
            </button>
            <button
              onClick={() => { setActiveTab('resultados'); cancelEdit(); }}
              className={`flex-1 text-sm font-medium py-2 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'resultados' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-bar-chart-line mr-1.5"></i>
              Resultados
            </button>
          </div>

          {/* Tab: Info (Endereço / PIX / Amostra) */}
          {activeTab === 'info' && client && (
            <div className="space-y-4">
              {/* Contato */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4 flex-wrap">
                  {editSection !== 'contato' ? (
                    <>
                      {client.phone && (
                        <a
                          href={`https://wa.me/${client.phone.replace(/\\D/g, '')}`}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 border border-emerald-100 rounded-lg text-sm text-emerald-700 font-medium transition-all cursor-pointer"
                        >
                          <i className="ri-whatsapp-line text-sm"></i>
                          {client.phone}
                        </a>
                      )}
                      {client.email && (
                        <span className="inline-flex items-center gap-2 text-sm text-gray-500">
                          <i className="ri-mail-line text-sm text-gray-400"></i>
                          {client.email}
                        </span>
                      )}
                      {client.cpf_cnpj && (
                        <span className="inline-flex items-center gap-2 text-sm text-gray-500">
                          <i className="ri-file-text-line text-sm text-gray-400"></i>
                          <span className="font-mono text-xs">{client.cpf_cnpj}</span>
                        </span>
                      )}
                    </>
                  ) : null}
                </div>
                {editSection !== 'contato' && <EditButton section="contato" />}
              </div>

              {/* Contato - Edição */}
              {editSection === 'contato' && (
                <div className="bg-teal-50/30 border border-teal-100 rounded-xl p-4 space-y-3 animate-[fadeIn_0.15s_ease-out]">
                  <div className="flex items-center gap-2 mb-1">
                    <i className="ri-pencil-line text-xs text-teal-500"></i>
                    <span className="text-xs font-semibold text-teal-700">Editando Contato</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={labelClass}>Telefone</label>
                      <input type="text" className={inputClass} value={(editData.phone as string) || ''} onChange={e => updateField('phone', e.target.value)} placeholder="(11) 99999-9999" />
                    </div>
                    <div>
                      <label className={labelClass}>E-mail</label>
                      <input type="email" className={inputClass} value={(editData.email as string) || ''} onChange={e => updateField('email', e.target.value)} placeholder="email@exemplo.com" />
                    </div>
                    <div>
                      <label className={labelClass}>CPF/CNPJ</label>
                      <input type="text" className={inputClass} value={(editData.cpf_cnpj as string) || ''} onChange={e => updateField('cpf_cnpj', e.target.value)} placeholder="000.000.000-00" />
                    </div>
                  </div>
                  <SaveCancelButtons />
                </div>
              )}

              {/* Endereço */}
              <div className={`border rounded-xl p-4 ${hasAddress ? 'bg-sky-50/40 border-sky-100' : 'bg-gray-50 border-gray-100'}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <i className={`ri-map-pin-line text-sm ${hasAddress ? 'text-sky-500' : 'text-gray-400'}`}></i>
                    Endereço
                  </h4>
                  {editSection !== 'endereco' && <EditButton section="endereco" />}
                </div>
                {editSection !== 'endereco' ? (
                  hasAddress ? (
                    <p className="text-sm text-gray-600">{fullAddress}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">Nenhum endereço cadastrado</p>
                  )
                ) : (
                  <div className="space-y-3 animate-[fadeIn_0.15s_ease-out]">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className={labelClass}>CEP</label>
                        <input type="text" className={inputClass} value={(editData.endereco_cep as string) || ''} onChange={e => updateField('endereco_cep', e.target.value)} placeholder="00000-000" />
                      </div>
                      <div className="col-span-2">
                        <label className={labelClass}>Rua</label>
                        <input type="text" className={inputClass} value={(editData.endereco_rua as string) || ''} onChange={e => updateField('endereco_rua', e.target.value)} placeholder="Nome da rua" />
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className={labelClass}>Número</label>
                        <input type="text" className={inputClass} value={(editData.endereco_numero as string) || ''} onChange={e => updateField('endereco_numero', e.target.value)} placeholder="Nº" />
                      </div>
                      <div>
                        <label className={labelClass}>Complemento</label>
                        <input type="text" className={inputClass} value={(editData.endereco_complemento as string) || ''} onChange={e => updateField('endereco_complemento', e.target.value)} placeholder="Apto, Bloco..." />
                      </div>
                      <div>
                        <label className={labelClass}>Bairro</label>
                        <input type="text" className={inputClass} value={(editData.endereco_bairro as string) || ''} onChange={e => updateField('endereco_bairro', e.target.value)} placeholder="Bairro" />
                      </div>
                      <div>
                        <label className={labelClass}>Cidade</label>
                        <input type="text" className={inputClass} value={(editData.endereco_cidade as string) || ''} onChange={e => updateField('endereco_cidade', e.target.value)} placeholder="Cidade" />
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className={labelClass}>Estado</label>
                        <input type="text" className={inputClass} value={(editData.endereco_estado as string) || ''} onChange={e => updateField('endereco_estado', e.target.value)} placeholder="UF" />
                      </div>
                    </div>
                    <SaveCancelButtons />
                  </div>
                )}
              </div>

              {/* PIX */}
              <div className={`border rounded-xl p-4 ${hasPix ? 'bg-emerald-50/40 border-emerald-100' : 'bg-gray-50 border-gray-100'}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <i className={`ri-bank-card-line text-sm ${hasPix ? 'text-emerald-500' : 'text-gray-400'}`}></i>
                    Chave PIX
                  </h4>
                  {editSection !== 'pix' && <EditButton section="pix" />}
                </div>
                {editSection !== 'pix' ? (
                  hasPix ? (
                    <div>
                      {client.chave_pix_tipo && (
                        <p className="text-[11px] text-emerald-600 font-medium mb-1">
                          {pixTipoLabel[client.chave_pix_tipo] || client.chave_pix_tipo}
                        </p>
                      )}
                      <p className="text-sm text-gray-700 font-mono bg-white/60 px-2.5 py-1.5 rounded-lg border border-emerald-100 break-all inline-block">
                        {client.chave_pix}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">Nenhuma chave PIX cadastrada</p>
                  )
                ) : (
                  <div className="space-y-3 animate-[fadeIn_0.15s_ease-out]">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>Tipo da Chave</label>
                        <select className={inputClass} value={(editData.chave_pix_tipo as string) || ''} onChange={e => updateField('chave_pix_tipo', e.target.value)}>
                          <option value="">Selecione...</option>
                          <option value="cpf">CPF</option>
                          <option value="cnpj">CNPJ</option>
                          <option value="email">E-mail</option>
                          <option value="telefone">Telefone</option>
                          <option value="aleatoria">Chave Aleatória</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Chave PIX</label>
                        <input type="text" className={inputClass} value={(editData.chave_pix as string) || ''} onChange={e => updateField('chave_pix', e.target.value)} placeholder="Informe a chave PIX" />
                      </div>
                    </div>
                    <SaveCancelButtons />
                  </div>
                )}
              </div>

              {/* Amostra */}
              <div className={`border rounded-xl p-4 ${client.amostra_enviada ? 'bg-emerald-50/40 border-emerald-100' : hasSample ? 'bg-amber-50/40 border-amber-100' : 'bg-gray-50 border-gray-100'}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <i className={`ri-gift-line text-sm ${hasSample ? 'text-amber-500' : 'text-gray-400'}`}></i>
                    Amostra de Produto
                  </h4>
                  <div className="flex items-center gap-1">
                    {editSection !== 'amostra' && deal?.client_id && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSampleModal(true);
                        }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-white bg-[#004aad] hover:bg-[#003d91] rounded-md transition-all cursor-pointer whitespace-nowrap shadow-sm"
                        title="Solicitar Amostra"
                      >
                        <i className="ri-send-plane-line text-xs"></i>
                        Solicitar Amostra
                      </button>
                    )}
                    {editSection !== 'amostra' && <EditButton section="amostra" />}
                  </div>
                </div>
                {editSection !== 'amostra' ? (
                  hasSample ? (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md ${client.amostra_enviada ? 'text-emerald-600 bg-emerald-100' : 'text-amber-600 bg-amber-100'}`}>
                          <i className={`text-xs ${client.amostra_enviada ? 'ri-check-line' : 'ri-time-line'}`}></i>
                          {client.amostra_enviada ? 'Enviada' : 'Pendente / Em trânsito'}
                        </span>
                        {client.amostra_data_envio && (
                          <span className="text-[11px] text-gray-500">
                            em {new Date(client.amostra_data_envio).toLocaleDateString('pt-BR')}
                          </span>
                        )}
                      </div>
                      {client.codigo_rastreio && (
                        <div className="mt-2">
                          <p className="text-[11px] text-gray-500 mb-0.5">Código de rastreio:</p>
                          <p className="text-xs text-gray-700 font-mono bg-white/60 px-2.5 py-1.5 rounded-lg border border-gray-200 break-all inline-block">
                            {client.codigo_rastreio}
                          </p>
                        </div>
                      )}
                      {client.amostra_observacao && (
                        <p className="text-xs text-gray-500 mt-2 italic">{client.amostra_observacao}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">Nenhuma amostra registrada</p>
                  )
                ) : (
                  <div className="space-y-3 animate-[fadeIn_0.15s_ease-out]">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(editData.amostra_enviada as boolean) || false}
                          onChange={e => updateField('amostra_enviada', e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-teal-500 focus:ring-teal-500 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700 font-medium">Amostra enviada</span>
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>Código de Rastreio</label>
                        <input type="text" className={inputClass} value={(editData.codigo_rastreio as string) || ''} onChange={e => updateField('codigo_rastreio', e.target.value)} placeholder="Código de rastreio" />
                      </div>
                      <div>
                        <label className={labelClass}>Data de Envio</label>
                        <input type="date" className={inputClass} value={(editData.amostra_data_envio as string) || ''} onChange={e => updateField('amostra_data_envio', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Observação</label>
                      <input type="text" className={inputClass} value={(editData.amostra_observacao as string) || ''} onChange={e => updateField('amostra_observacao', e.target.value)} placeholder="Observação sobre a amostra" />
                    </div>
                    <SaveCancelButtons />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tab: Resultados */}
          {activeTab === 'resultados' && client && (
            <div className="space-y-5">
              {/* Botão editar resultados */}
              <div className="flex justify-end">
                {editSection !== 'resultados' && <EditButton section="resultados" />}
              </div>

              {editSection !== 'resultados' ? (
                <>
                  {/* GMV Geral */}
                  <div className="bg-gradient-to-r from-[#5de0e6]/20 to-emerald-50 border border-[#5de0e6]/20 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[#004aad] font-medium">GMV Geral</p>
                      <p className="text-2xl font-bold text-gray-900 mt-0.5">{formatCurrency(client.gmv_geral)}</p>
                    </div>
                    <div className="w-12 h-12 bg-[#5de0e6]/20 rounded-xl flex items-center justify-center">
                      <i className="ri-money-dollar-circle-line text-xl text-[#004aad]"></i>
                    </div>
                  </div>

                  {/* Comissões */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs text-gray-400 mb-1">Comissão Orgânica</p>
                      <p className="text-xl font-bold text-gray-900">{Number(client.comissao_organica || 0)}%</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs text-gray-400 mb-1">Comissão Tráfego</p>
                      <p className="text-xl font-bold text-gray-900">{Number(client.comissao_trafego || 0)}%</p>
                    </div>
                  </div>

                  {/* GMV Interno */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <i className="ri-money-dollar-circle-line text-emerald-500 text-sm"></i>
                      GMV Interno
                    </h4>
                    <div className="grid grid-cols-4 gap-3">
                      {gmvData.map((item, i) => (
                        <div key={i} className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-3 text-center">
                          <p className="text-[11px] text-emerald-600 font-medium mb-1">{item.label}</p>
                          <p className="text-sm font-bold text-gray-900">{formatCurrency(Number(item.value || 0))}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Vídeos e Lives */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <i className="ri-video-line text-teal-500 text-sm"></i>
                        Vídeos Feitos
                      </h4>
                      <div className="space-y-2">
                        {videosData.map((item, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <span className="text-xs text-gray-400 w-8">{item.label}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-teal-400 to-teal-500 rounded-full flex items-center justify-end pr-2 transition-all"
                                style={{ width: `${Math.max((Number(item.value || 0) / maxVideos) * 100, 8)}%` }}
                              >
                                <span className="text-[10px] font-bold text-white">{Number(item.value || 0)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <i className="ri-live-line text-rose-500 text-sm"></i>
                        Lives Feitas
                      </h4>
                      <div className="space-y-2">
                        {livesData.map((item, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <span className="text-xs text-gray-400 w-8">{item.label}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-rose-400 to-rose-500 rounded-full flex items-center justify-end pr-2 transition-all"
                                style={{ width: `${Math.max((Number(item.value || 0) / maxLives) * 100, 8)}%` }}
                              >
                                <span className="text-[10px] font-bold text-white">{Number(item.value || 0)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Produtos Divulgados */}
                  {client.produtos_divulgados && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <i className="ri-shopping-bag-line text-teal-500 text-sm"></i>
                        Produtos Divulgados
                      </h4>
                      <div className="flex flex-wrap gap-1.5">
                        {client.produtos_divulgados.split(',').map((p, i) => (
                          <span key={i} className="px-2.5 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-md border border-teal-100">
                            {p.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* TikTok Links */}
                  {client.tiktok_links && client.tiktok_links.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <i className="ri-tiktok-line text-gray-800 text-sm"></i>
                        Contas TikTok ({client.tiktok_links.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {client.tiktok_links.map((link, i) => (
                          <a key={i} href={link} target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-100 rounded-lg text-sm text-gray-700 transition-all cursor-pointer">
                            <i className="ri-tiktok-line text-sm"></i>
                            <span className="truncate max-w-[180px]">{link.replace('https://tiktok.com/', '').replace('https://www.tiktok.com/', '')}</span>
                            <i className="ri-external-link-line text-xs text-gray-400"></i>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* WhatsApp Group */}
                  {client.whatsapp_group_link && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <i className="ri-whatsapp-line text-emerald-500 text-sm"></i>
                        Grupo do WhatsApp
                      </h4>
                      <a href={client.whatsapp_group_link} target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-100 rounded-lg text-sm text-emerald-700 font-medium transition-all cursor-pointer">
                        <i className="ri-external-link-line text-sm"></i>
                        Abrir grupo
                      </a>
                    </div>
                  )}
                </>
              ) : (
                /* Edição de Resultados */
                <div className="space-y-4 animate-[fadeIn_0.15s_ease-out]">
                  <div className="flex items-center gap-2 mb-1">
                    <i className="ri-pencil-line text-xs text-teal-500"></i>
                    <span className="text-xs font-semibold text-teal-700">Editando Resultados</span>
                  </div>

                  {/* GMV e Comissões */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={labelClass}>GMV Geral (R$)</label>
                      <input type="number" step="0.01" className={inputClass} value={Number(editData.gmv_geral || 0)} onChange={e => updateField('gmv_geral', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <label className={labelClass}>Comissão Orgânica (%)</label>
                      <input type="number" step="0.1" className={inputClass} value={Number(editData.comissao_organica || 0)} onChange={e => updateField('comissao_organica', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <label className={labelClass}>Comissão Tráfego (%)</label>
                      <input type="number" step="0.1" className={inputClass} value={Number(editData.comissao_trafego || 0)} onChange={e => updateField('comissao_trafego', parseFloat(e.target.value) || 0)} />
                    </div>
                  </div>

                  {/* GMV Interno */}
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-2">GMV Interno (R$)</p>
                    <div className="grid grid-cols-4 gap-3">
                      {gmvData.map((item) => (
                        <div key={item.field}>
                          <label className={labelClass}>{item.label}</label>
                          <input type="number" step="0.01" className={inputClass} value={Number((editData as Record<string, unknown>)[item.field] || 0)} onChange={e => updateField(item.field, parseFloat(e.target.value) || 0)} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Vídeos */}
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-2">Vídeos Feitos</p>
                    <div className="grid grid-cols-4 gap-3">
                      {videosData.map((item) => (
                        <div key={item.field}>
                          <label className={labelClass}>{item.label}</label>
                          <input type="number" className={inputClass} value={Number((editData as Record<string, unknown>)[item.field] || 0)} onChange={e => updateField(item.field, parseInt(e.target.value) || 0)} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Lives */}
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-2">Lives Feitas</p>
                    <div className="grid grid-cols-4 gap-3">
                      {livesData.map((item) => (
                        <div key={item.field}>
                          <label className={labelClass}>{item.label}</label>
                          <input type="number" className={inputClass} value={Number((editData as Record<string, unknown>)[item.field] || 0)} onChange={e => updateField(item.field, parseInt(e.target.value) || 0)} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Produtos e Links */}
                  <div>
                    <label className={labelClass}>Produtos Divulgados (separados por vírgula)</label>
                    <input type="text" className={inputClass} value={(editData.produtos_divulgados as string) || ''} onChange={e => updateField('produtos_divulgados', e.target.value)} placeholder="Produto 1, Produto 2, ..." />
                  </div>

                  <div>
                    <label className={labelClass}>Link do Grupo WhatsApp</label>
                    <input type="text" className={inputClass} value={(editData.whatsapp_group_link as string) || ''} onChange={e => updateField('whatsapp_group_link', e.target.value)} placeholder="https://chat.whatsapp.com/..." />
                  </div>

                  <div>
                    <label className={labelClass}>Contas TikTok (uma por linha)</label>
                    <textarea
                      className={`${inputClass} min-h-[80px]`}
                      value={((editData.tiktok_links as string[]) || []).join('\n')}
                      onChange={e => updateField('tiktok_links', e.target.value.split('\n').filter(l => l.trim()))}
                      placeholder={"https://www.tiktok.com/@usuario1\nhttps://www.tiktok.com/@usuario2"}
                    />
                  </div>

                  <SaveCancelButtons />
                </div>
              )}
            </div>
          )}

          {/* Sem creator vinculado */}
          {!client && !loading && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mb-3">
                <i className="ri-user-unfollow-line text-2xl"></i>
              </div>
              <p className="text-sm font-medium">Nenhum creator vinculado</p>
              <p className="text-xs text-gray-300 mt-1">Edite o acompanhamento para vincular um creator</p>
            </div>
          )}
        </div>
      )}

      {/* Modal Solicitar Amostra */}
      {deal && deal.client_id && (
        <RequestSampleModal
          isOpen={showSampleModal}
          onClose={() => setShowSampleModal(false)}
          clientId={deal.client_id}
          dealId={deal.id}
          dealTitle={deal.title}
          onSuccess={async () => {
            await loadClient();
            if (onClientUpdated) await onClientUpdated();
          }}
        />
      )}
    </Modal>
  );
}